import React, { useEffect, useMemo, useState } from "react";
import Breadcrumb from "../../../components/breadcrumb/Breadcrumb";

import useCoupon from "../../../hooks/useCoupon";
import useUtilisateurs from "../../../hooks/useUtilisateur";
import useGroupeCategorie from "../../../hooks/useGroupeCategorie";
import MultiSelect from "../../../components/article/MultiSelect";
import "../../../assets/css/admin/gestionCoupon/AdminCouponPage.css";

export default function AdminCouponPage({ setAlert }) {
  const [utilisateurs, setUtilisateurs] = useState([]);
  const [groupes, setGroupes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchUtilisateur, setSearchUtilisateur] = useState("");
  const [searchCategorie, setSearchCategorie] = useState("");
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [couponsLoading, setCouponsLoading] = useState(false);
  const [maxUse, setMaxUse] = useState(0);
  const { fetchCoupons, createCoupon, updateCoupon, deleteCoupon } =
    useCoupon();

  const { getAllUser } = useUtilisateurs();

  const { fetchGroupes } = useGroupeCategorie();

  const [q, setQ] = useState("");
  const [archive, setArchive] = useState("all");
  const [active, setActive] = useState("all");
  const [sort, setSort] = useState("newest");

  const [page, setPage] = useState(1);
  const pageSize = 10;

  // ============================================================
  // MODAL
  // ============================================================

  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  // ============================================================
  // FORMULAIRE
  // ============================================================

  const emptyForm = {
    nom: "",
    pourcentage: "",
    dateDebut: "",
    dateFin: "",

    archive: 2,
    active: 1,

    // Vide = tous les utilisateurs
    cibleUtilisateur: [],

    // Vide = toutes les catégories / groupes catégories
    cibleCategorie: [],
    canAutoApply: 2,
    maxUse: null,
    maxUsePerUser: null,
  };

  const [form, setForm] = useState(emptyForm);

  // ============================================================
  // BREADCRUMB
  // ============================================================

  const breadcrumbItems = [
    { label: "Mon Compte", link: "/utilisateur/compte" },
    { label: "Admin", link: "/admin/dashboard" },
    { label: "Coupons" },
  ];

  const filteredUtilisateurs = useMemo(() => {
    const search = searchUtilisateur.toLowerCase().trim();
    if (!search) return utilisateurs;

    return utilisateurs.filter((user) => {
      const nom = String(user?.nom || "").toLowerCase();
      const prenom = String(user?.prenom || "").toLowerCase();
      const email = String(user?.email || "").toLowerCase();

      return (
        nom.includes(search) ||
        prenom.includes(search) ||
        email.includes(search)
      );
    });
  }, [utilisateurs, searchUtilisateur]);

  const filteredCategories = useMemo(() => {
    const search = searchCategorie.toLowerCase().trim();

    if (!search) return categories;

    return categories.filter((categorie) => {
      const nom = String(categorie?.nom || "").toLowerCase();

      return nom.includes(search);
    });
  }, [categories, searchCategorie]);

  const reloadCoupons = async () => {
    setCouponsLoading(true);
    await fetchCoupons()
      .then((res) => {
        // On accepte aussi une liste vide venant du serveur (ex: après suppression du dernier élément)
        if (res?.data?.data) {
          setCoupons(res.data.data);
        }
      })
      .catch((err) => {
        console.error(err);
      });
    setCouponsLoading(false);
  };

  // ============================================================
  // CHARGEMENT
  // ============================================================

  useEffect(() => {
    // Hierarchie de recuperatio: Categories -> Utilisateurs -> Coupons
    setLoading(true);
    fetchGroupes()
      .then((res) => {
        if (res?.data?.data?.length > 0) {
          setCategories(res.data.data);
        }
        getAllUser({ active: 1, archive: 2 })
          .then((res) => {
            if (res?.data?.data?.length > 0) {
              setUtilisateurs(res.data.data);
            }
            fetchCoupons()
              .then((res) => {
                if (res?.data?.data?.length > 0) {
                  setCoupons(res.data.data);
                }
                setLoading(false);
              })
              .catch((err) => {
                setLoading(false);
                console.error(err);
              });
          })
          .catch((err) => {
            setLoading(false);
            console.error(err);
          });
      })
      .catch((err) => {
        setLoading(false);
        console.error(err);
      });
  }, []);

  // ============================================================
  // OUVRIR CREATION
  // ============================================================

  const openCreate = () => {
    setEditId(null);

    setForm({
      ...emptyForm,
      cibleUtilisateur: [],
      cibleCategorie: [],
    });

    setSearchUtilisateur("");
    setSearchCategorie("");

    setModalOpen(true);
  };

  // ============================================================
  // OUVRIR MODIFICATION
  // ============================================================

  const openEdit = (coupon) => {
    setEditId(coupon?._id);

    setForm({
      nom: coupon?.nom ?? "",
      pourcentage: coupon?.pourcentage ?? "",

      dateDebut: coupon?.dateDebut ? formatDateTimeLocal(coupon.dateDebut) : "",

      dateFin: coupon?.dateFin ? formatDateTimeLocal(coupon.dateFin) : "",

      archive: Number(coupon?.archive ?? 2),
      active: Number(coupon?.active ?? 1),

      cibleUtilisateur: (coupon?.cibleUtilisateur || []).map((id) =>
        String(id?._id || id),
      ),

      cibleCategorie: (coupon?.cibleCategorie || []).map((id) =>
        String(id?._id || id),
      ),

      canAutoApply: Number(coupon?.canAutoApply ?? 2),
      maxUse: coupon?.maxUse ?? null,
      maxUsePerUser: coupon?.maxUsePerUser ?? null,
    });

    setSearchUtilisateur("");
    setSearchCategorie("");

    setModalOpen(true);
  };

  // ============================================================
  // FERMER MODAL
  // ============================================================

  const closeModal = () => {
    if (saving) return;

    setModalOpen(false);
  };

  // ============================================================
  // FORMAT DATE POUR INPUT DATETIME-LOCAL
  // ============================================================

  function formatDateTimeLocal(date) {
    const d = new Date(date);

    if (Number.isNaN(d.getTime())) return "";

    const pad = (n) => String(n).padStart(2, "0");

    return (
      `${d.getFullYear()}-` +
      `${pad(d.getMonth() + 1)}-` +
      `${pad(d.getDate())}T` +
      `${pad(d.getHours())}:` +
      `${pad(d.getMinutes())}`
    );
  }

  // ============================================================
  // LABELS
  // ============================================================

  const archiveLabel = (value) =>
    Number(value) === 1 ? "Archivé" : "Non archivé";

  const activeLabel = (value) => (Number(value) === 1 ? "Actif" : "Inactif");

  const cibleUtilisateurLabel = (coupon) => {
    const type = coupon?.cibleUtilisateur?.type;

    if (type === "utilisateurs") {
      return "Utilisateurs sélectionnés";
    }

    if (type === "groupes") {
      return "Groupes sélectionnés";
    }

    return "Tous les utilisateurs";
  };

  const cibleCategorieLabel = (coupon) => {
    const type = coupon?.cibleCategorie?.type;

    if (type === "categories") {
      return "Catégories sélectionnées";
    }

    return "Toutes les catégories";
  };

  // ============================================================
  // FILTRAGE / TRI
  // ============================================================

  const filtered = useMemo(() => {
    const nq = String(q || "")
      .toLowerCase()
      .trim();

    return [...(coupons || [])]
      .filter((coupon) => {
        const okQ =
          !nq ||
          String(coupon?.nom || "")
            .toLowerCase()
            .includes(nq);

        const okArchive =
          archive === "all"
            ? true
            : Number(coupon?.archive) === Number(archive);

        const okActive =
          active === "all" ? true : Number(coupon?.active) === Number(active);

        return okQ && okArchive && okActive;
      })
      .sort((a, b) => {
        const da = new Date(a?.createdAt || 0).getTime();
        const db = new Date(b?.createdAt || 0).getTime();

        if (sort === "newest") return db - da;
        if (sort === "oldest") return da - db;

        const na = String(a?.nom || "").toLowerCase();
        const nb = String(b?.nom || "").toLowerCase();

        if (sort === "nom_asc") {
          return na.localeCompare(nb);
        }

        if (sort === "nom_desc") {
          return nb.localeCompare(na);
        }

        return 0;
      });
  }, [coupons, q, archive, active, sort]);

  // ============================================================
  // PAGINATION
  // ============================================================

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

  useEffect(() => {
    setPage(1);
  }, [q, archive, active, sort]);

  // Recale la page courante si elle dépasse le nouveau total
  // (utile après une suppression optimiste qui vide la dernière page)
  useEffect(() => {
    setPage((p) => Math.min(p, totalPages));
  }, [totalPages]);

  const pageItems = useMemo(() => {
    const start = (page - 1) * pageSize;

    return filtered.slice(start, start + pageSize);
  }, [filtered, page]);

  // ============================================================
  // MODIFICATION CIBLE UTILISATEUR
  // ============================================================

  const setCibleUtilisateurType = (type) => {
    setForm((prev) => ({
      ...prev,
      cibleUtilisateur: {
        type,
        utilisateurs:
          type === "utilisateurs" ? prev.cibleUtilisateur.utilisateurs : [],
        groupes: type === "groupes" ? prev.cibleUtilisateur.groupes : [],
      },
    }));
  };

  // ============================================================
  // MODIFICATION UTILISATEURS MULTISELECT
  // ============================================================

  const toggleUtilisateur = (id) => {
    setForm((prev) => {
      const current = prev.cibleUtilisateur || [];

      const exists = current.includes(id);

      return {
        ...prev,
        cibleUtilisateur: exists
          ? current.filter((item) => item !== id)
          : [...current, id],
      };
    });
  };

  // ============================================================
  // MODIFICATION GROUPES MULTISELECT
  // ============================================================

  const toggleGroupe = (id) => {
    setForm((prev) => {
      const current = prev.cibleUtilisateur.groupes;

      const exists = current.includes(id);

      return {
        ...prev,
        cibleUtilisateur: {
          ...prev.cibleUtilisateur,
          groupes: exists
            ? current.filter((item) => item !== id)
            : [...current, id],
        },
      };
    });
  };

  // ============================================================
  // MODIFICATION CIBLE CATEGORIE
  // ============================================================

  const setCibleCategorieType = (type) => {
    setForm((prev) => ({
      ...prev,
      cibleCategorie: {
        type,
        categories: type === "categories" ? prev.cibleCategorie.categories : [],
      },
    }));
  };

  // ============================================================
  // MULTISELECT CATEGORIES
  // ============================================================

  const toggleCategorie = (id) => {
    setForm((prev) => {
      const current = prev.cibleCategorie || [];

      const exists = current.includes(id);

      return {
        ...prev,
        cibleCategorie: exists
          ? current.filter((item) => item !== id)
          : [...current, id],
      };
    });
  };

  // ============================================================
  // SUBMIT (CREATE / UPDATE) — mise à jour optimiste en temps réel
  // ============================================================

  const onSubmit = async (e) => {
    e.preventDefault();

    const nom = String(form.nom || "").trim();

    if (!nom) {
      setAlert?.({
        type: "warning",
        message: "Le champ 'Nom' est requis.",
      });

      return;
    }

    const pourcentage = Number(form.pourcentage);

    if (
      form.pourcentage === "" ||
      Number.isNaN(pourcentage) ||
      pourcentage <= 0 ||
      pourcentage > 100
    ) {
      setAlert?.({
        type: "warning",
        message: "Le pourcentage doit être un nombre entre 1 et 100.",
      });
      return;
    }

    if (!form.dateDebut) {
      setAlert?.({
        type: "warning",
        message: "La date de début est requise.",
      });

      return;
    }

    if (!form.dateFin) {
      setAlert?.({
        type: "warning",
        message: "La date de fin est requise.",
      });

      return;
    }

    if (!form.canAutoApply) {
      setAlert?.({
        type: "warning",
        message: "Le champ 'Application automatique' est requis.",
      });

      return;
    }

    if (
      form.maxUse !== null &&
      (form.maxUse === "" ||
        !Number.isInteger(Number(form.maxUse)) ||
        Number(form.maxUse) < 1)
    ) {
      setAlert?.({
        type: "warning",
        message:
          "Le nombre maximal d'utilisations doit être un entier supérieur à 0.",
      });

      return;
    }

    const limiteInvalide = (v) =>
      v !== null && (v === "" || !Number.isInteger(Number(v)) || Number(v) < 1);

    if (limiteInvalide(form.maxUsePerUser)) {
      setAlert?.({
        type: "warning",
        message:
          "Le nombre d'utilisations par utilisateur doit être un entier supérieur à 0.",
      });
      return;
    }

    const debut = new Date(form.dateDebut);
    const fin = new Date(form.dateFin);

    if (fin <= debut) {
      setAlert?.({
        type: "warning",
        message: "La date de fin doit être supérieure à la date de début.",
      });

      return;
    }

    if (
      form.cibleUtilisateur.type === "utilisateurs" &&
      form.cibleUtilisateur.utilisateurs.length === 0
    ) {
      setAlert?.({
        type: "warning",
        message: "Sélectionnez au moins un utilisateur.",
      });

      return;
    }

    if (
      form.cibleUtilisateur.type === "groupes" &&
      form.cibleUtilisateur.groupes.length === 0
    ) {
      setAlert?.({
        type: "warning",
        message: "Sélectionnez au moins un groupe d'utilisateurs.",
      });

      return;
    }

    if (
      form.cibleCategorie.type === "categories" &&
      form.cibleCategorie.categories.length === 0
    ) {
      setAlert?.({
        type: "warning",
        message: "Sélectionnez au moins une catégorie.",
      });

      return;
    }

    const payload = {
      nom,
      pourcentage,

      dateDebut: debut.toISOString(),
      dateFin: fin.toISOString(),

      archive: Number(form.archive),
      active: Number(form.active),

      cibleUtilisateur: form.cibleUtilisateur || [],

      cibleCategorie: form.cibleCategorie || [],
      canAutoApply: Number(form.canAutoApply) || 2,
      maxUse: form.maxUse !== null ? Number(form.maxUse) : null,
      maxUsePerUser:
        form.maxUsePerUser !== null ? Number(form.maxUsePerUser) : null,
    };

    setSaving(true);

    try {
      if (editId) {
        const res = await updateCoupon(editId, payload);

        // On récupère le coupon renvoyé par l'API si disponible,
        // sinon on fusionne le payload localement pour un rendu instantané.
        const updated = res?.data?.data;

        setCoupons((prev) =>
          prev.map((c) =>
            String(c?._id) === String(editId)
              ? { ...c, ...(updated || payload) }
              : c,
          ),
        );

        setAlert?.({
          type: "success",
          message: "Coupon modifié avec succès.",
        });
      } else {
        const res = await createCoupon(payload);

        const created = res?.data?.data;

        if (created) {
          // Ajout instantané en tête de liste
          setCoupons((prev) => [created, ...prev]);
        } else {
          // Filet de sécurité si l'API ne renvoie pas l'objet créé
          await reloadCoupons();
        }

        setAlert?.({
          type: "success",
          message: "Coupon créé avec succès.",
        });
      }

      setModalOpen(false);
      setForm(emptyForm);
      setEditId(null);
    } catch (err) {
      console.error(err);

      setAlert?.({
        type: "danger",
        message: "Échec de l'enregistrement du coupon.",
      });
    } finally {
      setSaving(false);
    }
  };

  // ============================================================
  // ARCHIVAGE — optimiste avec rollback
  // ============================================================

  const onToggleArchive = async (coupon) => {
    const next = Number(coupon?.archive) === 1 ? 2 : 1;
    const previousCoupons = coupons;

    // 1. Mise à jour immédiate de l'UI
    setCoupons((prev) =>
      prev.map((c) =>
        String(c?._id) === String(coupon?._id) ? { ...c, archive: next } : c,
      ),
    );

    try {
      await updateCoupon(coupon?._id, { archive: next });

      setAlert?.({
        type: "success",
        message: next === 1 ? "Coupon archivé." : "Coupon réactivé.",
      });
    } catch (err) {
      console.error(err);

      // 2. Rollback si l'API échoue
      setCoupons(previousCoupons);

      setAlert?.({
        type: "danger",
        message: "Impossible de modifier l'archive.",
      });
    }
  };

  // ============================================================
  // ACTIVATION / DESACTIVATION — optimiste avec rollback
  // ============================================================

  const onToggleActive = async (coupon) => {
    const next = Number(coupon?.active) === 1 ? 2 : 1;
    const previousCoupons = coupons;

    setCoupons((prev) =>
      prev.map((c) =>
        String(c?._id) === String(coupon?._id) ? { ...c, active: next } : c,
      ),
    );

    try {
      await updateCoupon(coupon?._id, { active: next });

      setAlert?.({
        type: "success",
        message: next === 1 ? "Coupon activé." : "Coupon désactivé.",
      });
    } catch (err) {
      console.error(err);

      setCoupons(previousCoupons);

      setAlert?.({
        type: "danger",
        message: "Impossible de modifier le statut.",
      });
    }
  };

  // ============================================================
  // SUPPRESSION — optimiste avec rollback
  // ============================================================

  const onDelete = async (coupon) => {
    const ok = window.confirm(`Supprimer le coupon "${coupon?.nom || ""}" ?`);

    if (!ok) return;

    const previousCoupons = coupons;

    // 1. Retrait immédiat de la liste
    setCoupons((prev) =>
      prev.filter((c) => String(c?._id) !== String(coupon?._id)),
    );

    try {
      await deleteCoupon(coupon?._id);

      setAlert?.({
        type: "success",
        message: "Coupon supprimé.",
      });
    } catch (err) {
      console.error(err);

      // 2. Rollback si la suppression échoue côté serveur
      setCoupons(previousCoupons);

      setAlert?.({
        type: "danger",
        message: "Suppression impossible.",
      });
    }
  };

  // ============================================================
  // RENDU
  // ============================================================

  return (
    <div className="adminGroupPage">
      <div className="container">
        <Breadcrumb items={breadcrumbItems} />

        {/* HEADER */}
        <div className="adminGroupHeader">
          <div>
            <div className="adminGroupTitle">Gestion des coupons</div>

            <div className="adminGroupSubtitle">
              {couponsLoading
                ? "Chargement..."
                : "Créer, modifier et gérer les coupons promotionnels."}
            </div>
          </div>

          <div className="adminGroupActions">
            <button
              className="adminBtn adminBtnSoft"
              type="button"
              onClick={() => reloadCoupons()}
              disabled={couponsLoading}
            >
              <i className="bi bi-arrow-clockwise" /> Rafraîchir
            </button>

            <button
              className="adminBtn adminBtnPrimary"
              type="button"
              onClick={openCreate}
            >
              <i className="bi bi-plus-circle" /> Nouveau coupon
            </button>
          </div>
        </div>

        {/* FILTRES */}
        <div className="adminGroupFilters">
          <div className="filterItem">
            <label>Recherche</label>

            <input
              className="form-control"
              placeholder="Rechercher par nom..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>

          <div className="filterItem">
            <label>Archive</label>

            <select
              className="form-select"
              value={archive}
              onChange={(e) => setArchive(e.target.value)}
            >
              <option value="all">Tous</option>

              <option value="2">Non archivés</option>

              <option value="1">Archivés</option>
            </select>
          </div>

          <div className="filterItem">
            <label>Statut</label>

            <select
              className="form-select"
              value={active}
              onChange={(e) => setActive(e.target.value)}
            >
              <option value="all">Tous</option>

              <option value="1">Actifs</option>

              <option value="2">Inactifs</option>
            </select>
          </div>

          <div className="filterItem">
            <label>Tri</label>

            <select
              className="form-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
            >
              <option value="newest">Plus récent</option>

              <option value="oldest">Plus ancien</option>

              <option value="nom_asc">Nom (A→Z)</option>

              <option value="nom_desc">Nom (Z→A)</option>
            </select>
          </div>
        </div>

        {/* TABLE */}
        <div className="adminGroupTableWrap">
          {couponsLoading ? (
            <div className="adminEmpty">
              <i className="bi bi-hourglass-split" />
              Chargement...
            </div>
          ) : filtered.length === 0 ? (
            <div className="adminEmpty">
              <i className="bi bi-ticket-perforated" />
              Aucun coupon trouvé.
            </div>
          ) : (
            <div className="adminCategoriesTableWrap">
              <table className="adminCategoriesTable">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Pourcentage</th>
                    <th>Validité</th>
                    <th>Limite d'utilisation</th>
                    <th>Cible utilisateur</th>
                    <th>Cible catégorie</th>
                    <th>Auto complète</th>
                    <th>Statut</th>
                    <th className="colActions">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {pageItems.map((coupon) => {
                    const a = Number(coupon?.archive ?? 2);
                    const act = Number(coupon?.active ?? 1);

                    return (
                      <tr key={coupon?._id}>
                        {/* NOM */}
                        <td>
                          <div className="rowTitle">{coupon?.nom || "—"}</div>
                        </td>

                        {/* POURCENTAGE */}
                        <td>
                          {coupon?.pourcentage != null &&
                          coupon?.pourcentage !== "" ? (
                            <span className="pill pillPercent">
                              {coupon.pourcentage}%
                            </span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>

                        {/* VALIDITE */}
                        <td>
                          <div className="validityDates">
                            <span>
                              {coupon?.dateDebut
                                ? new Date(coupon.dateDebut).toLocaleDateString(
                                    "fr-FR",
                                  )
                                : "—"}
                            </span>
                            <i className="bi bi-arrow-right" />
                            <span>
                              {coupon?.dateFin
                                ? new Date(coupon.dateFin).toLocaleDateString(
                                    "fr-FR",
                                  )
                                : "—"}
                            </span>
                          </div>
                        </td>

                        <td>
                          <div className="d-flex flex-column gap-1 align-items-start">
                            <span className="pill pillNeutral">
                              Global :{" "}
                              {coupon?.maxUse == null
                                ? "Illimité"
                                : `${coupon.useCount ?? 0}/${coupon.maxUse}`}
                            </span>
                            <span className="pill pillNeutral">
                              Par compte :{" "}
                              {coupon?.maxUsePerUser == null
                                ? "Illimité"
                                : coupon.maxUsePerUser}
                            </span>
                          </div>
                        </td>

                        {/* CIBLE UTILISATEUR */}
                        <td>
                          <span className="pill pillNeutral">
                            {cibleUtilisateurLabel(coupon)}
                          </span>
                        </td>

                        {/* CIBLE CATEGORIE */}
                        <td>
                          <span className="pill pillNeutral">
                            {cibleCategorieLabel(coupon)}
                          </span>
                        </td>

                        {/* Auto complète */}
                        <td>
                          <span className="pill pillNeutral">
                            {coupon?.canAutoApply == 1 ? "Oui" : "Non"}
                          </span>
                        </td>

                        {/* STATUT */}
                        <td>
                          <div className="d-flex flex-column gap-1 align-items-start">
                            <span
                              className={`pill ${act === 1 ? "pillGood" : "pillDanger"}`}
                            >
                              {activeLabel(act)}
                            </span>
                            {a === 1 && (
                              <span className="pill pillArchived">Archivé</span>
                            )}
                          </div>
                        </td>

                        {/* ACTIONS */}
                        <td>
                          <div className="rowActions">
                            <button
                              className="btnAction btnAction--edit"
                              onClick={() => openEdit(coupon)}
                              title="Modifier"
                            >
                              <i className="bi bi-pencil-square" />
                            </button>

                            <button
                              className="btnAction btnAction--toggle"
                              onClick={() => onToggleActive(coupon)}
                              title="Activer / Désactiver"
                            >
                              <i
                                className={`bi ${act === 1 ? "bi-toggle-on" : "bi-toggle-off"}`}
                              />
                            </button>

                            <button
                              className="btnAction btnAction--archive"
                              onClick={() => onToggleArchive(coupon)}
                              title="Archiver / Réactiver"
                            >
                              <i
                                className={`bi ${a === 1 ? "bi-arrow-counterclockwise" : "bi-archive"}`}
                              />
                            </button>

                            <button
                              className="btnAction btnAction--delete"
                              onClick={() => onDelete(coupon)}
                              title="Supprimer"
                            >
                              <i className="bi bi-trash" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* PAGINATION */}
        {filtered.length > 0 && (
          <div className="adminPagination">
            <button
              className="adminBtn adminBtnSoft"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <i className="bi bi-chevron-left" />
              Précédent
            </button>

            <div className="pageInfo">
              Page <strong>{page}</strong> / {totalPages} • {filtered.length}{" "}
              résultat(s)
            </div>

            <button
              className="adminBtn adminBtnSoft"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Suivant <i className="bi bi-chevron-right" />
            </button>
          </div>
        )}
      </div>

      {/* ====================================================== */}
      {/* MODAL */}
      {/* ====================================================== */}

      {modalOpen && (
        <div className="adminModalOverlay">
          <div className="adminModal" onClick={(e) => e.stopPropagation()}>
            {/* HEADER */}
            <div className="adminModalHeader">
              <div className="adminModalTitle">
                {editId ? "Modifier le coupon" : "Créer un coupon"}
              </div>

              <button
                className="adminModalClose"
                onClick={closeModal}
                disabled={saving}
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>

            <form onSubmit={onSubmit}>
              <div className="adminModalBody">
                {/* NOM */}
                <div className="mb-3">
                  <label className="form-label fw-bold">Nom du coupon</label>

                  <input
                    className="form-control"
                    value={form.nom}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        nom: e.target.value,
                      }))
                    }
                    placeholder="Ex : PROMO10"
                  />
                </div>

                {/* POURCENTAGE */}
                <div className="mb-3">
                  <label className="form-label fw-bold">
                    Pourcentage de réduction
                  </label>
                  <div className="input-group">
                    <input
                      type="number"
                      className="form-control"
                      value={form.pourcentage}
                      min={1}
                      max={100}
                      step={1}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, pourcentage: e.target.value }))
                      }
                      placeholder="Ex : 10"
                    />
                    <span className="input-group-text">%</span>
                  </div>
                </div>

                {/* NOMBRE MAXIMAL D'UTILISATIONS */}
                <div className="mb-3">
                  <label className="form-label fw-bold">
                    Nombre maximal d'utilisations
                  </label>

                  <select
                    className="form-select mb-2"
                    value={form.maxUse === null ? "unlimited" : "limited"}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        maxUse: e.target.value === "unlimited" ? null : 1,
                      }))
                    }
                  >
                    <option value="unlimited">Illimité</option>
                    <option value="limited">Nombre limité</option>
                  </select>

                  {form.maxUse !== null && (
                    <input
                      type="number"
                      className="form-control"
                      min={1}
                      step={1}
                      value={form.maxUse}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          maxUse: e.target.value,
                        }))
                      }
                      placeholder="Ex : 100"
                      required
                    />
                  )}

                  <small className="text-muted">
                    Illimité : aucune limite globale d'utilisation du coupon.
                  </small>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold">
                    Utilisations par utilisateur
                  </label>
                  <select
                    className="form-select mb-2"
                    value={
                      form.maxUsePerUser === null ? "unlimited" : "limited"
                    }
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        maxUsePerUser:
                          e.target.value === "unlimited" ? null : 1,
                      }))
                    }
                  >
                    <option value="unlimited">Illimité</option>
                    <option value="limited">Nombre limité</option>
                  </select>

                  {form.maxUsePerUser !== null && (
                    <input
                      type="number"
                      className="form-control"
                      min={1}
                      step={1}
                      value={form.maxUsePerUser}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          maxUsePerUser: e.target.value,
                        }))
                      }
                      placeholder="Ex : 10"
                      required
                    />
                  )}
                  <small className="text-muted">
                    Nombre de fois qu'un même compte peut utiliser ce coupon.
                  </small>
                </div>

                {/* DATES */}
                <div className="row g-3 mb-3">
                  <div className="col-12 col-md-6">
                    <label className="form-label fw-bold">Date de début</label>

                    <input
                      type="datetime-local"
                      className="form-control"
                      value={form.dateDebut}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          dateDebut: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label fw-bold">Date de fin</label>

                    <input
                      type="datetime-local"
                      className="form-control"
                      value={form.dateFin}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          dateFin: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>

                {/* ACTIVE / ARCHIVE / AAPLICATION*/}
                <div className="row g-3 mb-4">
                  <div className="col-12 col-md-4">
                    <label className="form-label fw-bold">Active</label>

                    <select
                      className="form-select"
                      value={form.active}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          active: Number(e.target.value),
                        }))
                      }
                    >
                      <option value={1}>Actif</option>

                      <option value={2}>Inactif</option>
                    </select>
                  </div>

                  <div className="col-12 col-md-4">
                    <label className="form-label fw-bold">Archive</label>

                    <select
                      className="form-select"
                      value={form.archive}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          archive: Number(e.target.value),
                        }))
                      }
                    >
                      <option value={2}>Non archivé</option>

                      <option value={1}>Archivé</option>
                    </select>
                  </div>
                  {/* APPLICATION AUTOMATIQUE */}
                  <div className="col-12 col-md-4">
                    <label className="form-label fw-bold">
                      Application automatique
                    </label>
                    <select
                      className="form-select"
                      value={form.canAutoApply}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          canAutoApply: Number(e.target.value),
                        }))
                      }
                    >
                      <option value={1}>Oui</option>

                      <option value={2}>Non</option>
                    </select>
                  </div>
                </div>

                {/* ================================================= */}
                {/* CIBLE UTILISATEUR */}
                {/* ================================================= */}

                <div className="mb-4">
                  <label className="form-label fw-semibold">
                    Utilisateurs concernés
                  </label>

                  <MultiSelect
                    options={utilisateurs}
                    value={form.cibleUtilisateur}
                    onChange={(values) =>
                      setForm((prev) => ({
                        ...prev,
                        cibleUtilisateur: values,
                      }))
                    }
                    placeholder="Tous les utilisateurs"
                    searchPlaceholder="Rechercher un utilisateur..."
                    getOptionValue={(user) => user?._id}
                    getOptionLabel={(user) =>
                      `${user?.nom || ""} ${user?.prenom || ""}${
                        user?.email ? ` - ${user.email}` : ""
                      }`
                    }
                  />
                </div>

                {/* ================================================= */}
                {/* CIBLE CATEGORIE */}
                {/* ================================================= */}

                <div className="mb-4">
                  <label className="form-label fw-semibold">
                    Catégories concernées
                  </label>

                  <MultiSelect
                    options={categories}
                    value={form.cibleCategorie}
                    onChange={(values) =>
                      setForm((prev) => ({
                        ...prev,
                        cibleCategorie: values,
                      }))
                    }
                    placeholder="Toutes les catégories"
                    searchPlaceholder="Rechercher une catégorie..."
                    getOptionValue={(categorie) => categorie?._id}
                    getOptionLabel={(categorie) => categorie?.nom || ""}
                  />
                </div>
              </div>

              {/* FOOTER */}
              <div className="adminModalFooter">
                <button
                  type="button"
                  className="adminBtn adminBtnSoft"
                  onClick={closeModal}
                  disabled={saving}
                >
                  Annuler
                </button>

                <button
                  type="submit"
                  className="adminBtn adminBtnPrimary"
                  disabled={saving}
                >
                  {saving ? "Enregistrement..." : editId ? "Modifier" : "Créer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
